import type { SearchResult } from '@elastic/search-ui'
import { Link, Tag } from 'components/common'
import { handleNavigate } from 'components/search/buckets/utils'
import { useStoreContext } from 'context/StoreProvider'
import { useMemo, useState } from 'react'
import { Row, Col, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import parse from 'html-react-parser'
import { codesystems, formatDate } from 'utils'
import highlightSearchTerm from 'components/search/buckets/utils/highlightSearchTerm'
import { CodeSystem } from 'context/elasticsearch/types'
import { useEsConfig } from 'context/elasticsearch/esConfigContext'
import {
	faChevronUp,
	faChevronDown,
	faClipboardCheck,
	faCopy,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const getRaw = (
	result: { [x: string]: { raw: string | boolean } },
	value: string | number
) => {
	if (
		!result[value] ||
		result[value].raw === undefined ||
		result[value].raw === null
	) {
		return
	}

	// Return bools as strings
	return `${result[value].raw}`
}

const isDate = (text: string) => {
	const datePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/
	return datePattern.test(text) && !isNaN(Date.parse(text))
}

const capitalizeFirstLetter = (value: string) => {
	if (value.startsWith('<em>')) {
		return value.slice(0, 4) + value.charAt(4).toUpperCase() + value.slice(5)
	}
	return value.charAt(0).toUpperCase() + value.slice(1)
}

function snippetFix(snippet?: string) {
	if (!snippet || typeof snippet !== 'string') return ''

	const regex = /\([^\)]+$/
	if (snippet.match(regex)) {
		return snippet + ')'
	}

	return capitalizeFirstLetter(snippet)
}

const processText = (
	field: string,
	searchTerm: string,
	useSnippet: boolean,
	result: SearchResult
) => {
	if (!result[field] || !result[field].raw) return null

	const snippet = result[field]?.snippet
	if (snippet && Array.isArray(snippet)) {
		return parse(snippetFix(snippet[0]))
	}

	const raw = result[field].raw
	if (typeof raw !== 'string') {
		return null
	}

	// If raw is a date, format it
	const formattedText = isDate(raw as string) ? formatDate(raw as string) : raw

	// If searchTerm is empty or undefined, return the original text
	if (!searchTerm) {
		return parse(formattedText)
	}

	// Highlight the search term in the formatted text
	const highlightedText = highlightSearchTerm(
		formattedText as string,
		searchTerm
	)

	// Parse the highlighted text into React elements
	const parsedText = parse(highlightedText)

	return parsedText
}

interface CodeSystemProp {
	codeSystem: CodeSystem
	displayScore: boolean
}

const SearchResultRow = ({
	result,
	searchTerm,
	addFilter,
	codeSystem,
	displayScore = false,
}: SearchResult & CodeSystemProp) => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { actions, state } = useStoreContext()
	const { code, scrollRef } = state
	const { config } = useEsConfig(codeSystem)

	const [showAllHits, setShowAllHits] = useState(true)
	const [showSynonymHits, setShowSynonymHits] = useState(false)

	const { updateCodeValue } = actions

	const handleScrollOnClick = () => {
		const scrollToElement = () => {
			const element = scrollRef.current[code as string]
			if (element) {
				element.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}
		}

		const delay = 500

		const timeoutId = setTimeout(scrollToElement, delay)

		return () => clearTimeout(timeoutId)
	}

	const handleReplacedByClick = () => {
		handleScrollOnClick()
		updateCodeValue(getRaw(result, 'replaced_by') as string)
		addFilter('active', 'true', 'any')
		navigate(
			`${handleNavigate(getRaw(result, 'code_system') as string)}/chapter/${
				getRaw(result, 'replaced_by') as string
			}`
		)
	}

	interface SynonymHit {
		term: string
		source_system: string
		source_id: string
		synonym_of_term: string
		score: number
	}

	// Memoize the computation of code hits to avoid recalculating on every render
	const { synonymHits, shouldShowSynonyms } = useMemo(() => {
		const hits: SynonymHit[] = []
		let shouldShow = false

		if (
			result._meta?.rawHit?.inner_hits?.synonyms?.hits?.hits &&
			Array.isArray(result._meta.rawHit.inner_hits.synonyms.hits.hits)
		) {
			const rawHits = result._meta.rawHit.inner_hits.synonyms.hits.hits
			for (const highHit of rawHits.filter(
				(hit: {
					_score: number
					_source?: {
						term?: string
						source_system?: string
						source_id?: string
						synonym_of_term?: string
					}
				}) => hit._score > config.minimumNestedFieldScore
			)) {
				if (highHit._source?.term) {
					const term = highlightSearchTerm(highHit._source.term, searchTerm)
					const sourceSystem = highHit._source.source_system || ''
					const sourceId = highHit._source.source_id || ''
					const synonymOfTerm = highHit._source.synonym_of_term || ''

					hits.push({
						term: term,
						source_system: sourceSystem,
						source_id: sourceId,
						synonym_of_term: synonymOfTerm,
						score: Math.round((highHit._score || 0) * 10) / 10,
					})
					shouldShow = true
				}
			}

			if (config.indexName.indexOf('-dev-') === -1) {
				shouldShow = false
			}
		}

		return { synonymHits: hits, shouldShowSynonyms: shouldShow }
	}, [
		result._meta?.rawHit?.inner_hits?.codes?.hits?.hits,
		searchTerm,
		config.minimumNestedFieldScore,
	])

	const totalHits = synonymHits.length

	const resultScore = useMemo(() => {
		return Math.round((parseFloat(result._meta.rawHit._score) || 0) * 10) / 10
	}, [result._meta.rawHit._score])

	const stopPointerEvent = (event: React.SyntheticEvent) => {
		event.stopPropagation()
	}

	const [isCopied, setIsCopied] = useState<boolean>(false)
	const [isHovered, setIsHovered] = useState<boolean>(false)

	const copyCode = (codeValue: string, name: string) => {
		navigator.clipboard.writeText(`${codeValue} ${name}`)
		setIsCopied(true)
	}

	const handleMouseLeave = () => {
		setIsHovered(false)
		if (!isCopied) return
		setIsCopied(false)
	}

	const handleMouseEnter = () => {
		setIsHovered(true)
	}

	return (
		<>
			<Row
				className='d-flex align-items-center gap-1 gap-md-3 w-100'
				role='row'
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{displayScore && (
					<Col xs='auto' style={{ width: '80px' }} role='cell' className='p-0'>
						<span>{resultScore}</span>
					</Col>
				)}
				<Col xs='auto' style={{ width: '80px' }} role='cell' className='p-0'>
					<span>
						{processText('code', searchTerm, false, result)}
						{
							getRaw(result, 'star_code') === 'true' && '*' // Add star if code is a star code
						}
					</span>
				</Col>
				<Col
					role='cell'
					className='text-wrap text-break p-0 d-flex gap-2 flex-column flex-md-row align-items-start align-items-md-center justify-content-between'
				>
					<Row className='d-flex gap-2 p-0'>
						<Col xs='auto' className='p-0'>
							{getRaw(result, 'name_norwegian') &&
								processText('name_norwegian', searchTerm, true, result)}
							<FontAwesomeIcon
								icon={isCopied ? faClipboardCheck : faCopy}
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									copyCode(
										getRaw(result, 'code') as string,
										getRaw(result, 'name_norwegian') as string
									)
								}}
								style={{
									paddingLeft: '0.5em',
									cursor: 'pointer',
									zIndex: 1,
									lineHeight: '1.5',
									color: `${isCopied ? '#047FA4' : 'grey'}`,
									display: isHovered ? 'inline' : 'none',
								}}
								className='copy-icon'
								title={
									isCopied
										? 'Kopiert til utklippstavle'
										: 'Kopier kodeverdi og -tekst'
								}
							/>
						</Col>
						{getRaw(result, 'active') === 'false' &&
							result.replaced_by &&
							result.replaced_by.raw &&
							result.code_system.raw && (
								<Col
									className='d-flex gap-1 flex-wrap'
									xs='auto'
									onClick={(event) => {
										event.stopPropagation()
										handleReplacedByClick()
									}}
									onKeyDown={(event) => {
										event.stopPropagation()
										if (event.key === 'Enter' || event.key === ' ') {
											handleReplacedByClick()
										}
									}}
									tabIndex={0}
								>
									{t('general.replacedBy')}:{' '}
									<Link
										text={getRaw(result, 'replaced_by') as string}
										href={`#`}
									/>
								</Col>
							)}
					</Row>
					{result.chapter_code &&
						result.chapter_code.raw &&
						result.code_system.raw === codesystems.phbu && (
							<Col xs='auto' className='p-0'>
								<Tag
									color={'yellow'}
									text={getRaw(result, 'chapter_code') as string}
								/>
							</Col>
						)}
					{getRaw(result, 'is_apat') &&
						getRaw(result, 'is_apat') === 'true' && (
							<Col xs='auto' className='p-0 ms-auto'>
								<Tag color={'yellow'} text={codesystems.apat} />
							</Col>
						)}

					{getRaw(result, 'active') === 'false' && (
						<Col xs='auto' className='p-0 ms-auto'>
							<Tag color={'red'} text={t('general.inactive')} />
						</Col>
					)}
				</Col>
			</Row>
			{shouldShowSynonyms && synonymHits.length > 0 && (
				<Row className='d-flex flex-column gap-0 p-0 m-0'>
					<div
						role='button'
						tabIndex={0}
						aria-expanded={showSynonymHits}
						aria-label={
							showSynonymHits
								? 'Skjul treff i synonymer'
								: 'Vis treff i synonymer'
						}
						style={{ cursor: 'pointer', fontSize: '0.8em' }}
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							setShowSynonymHits(!showSynonymHits)
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								e.stopPropagation()
								setShowSynonymHits(!showSynonymHits)
							}
						}}
						className='px-3 py-2'
					>
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<FontAwesomeIcon
								icon={showSynonymHits ? faChevronUp : faChevronDown}
								style={{ marginRight: '8px', color: '#6c757d' }}
							/>
							<span style={{ color: '#6c757d' }}>
								{showSynonymHits
									? 'Skjul treff i synonymer'
									: `Vis treff i synonymer (${totalHits})`}
							</span>
						</div>
					</div>
					{showSynonymHits && (
						<Table
							responsive
							className='p-0'
							hover
							size='sm'
							onClickCapture={stopPointerEvent}
							onDoubleClickCapture={stopPointerEvent}
							onContextMenuCapture={stopPointerEvent}
							onKeyDownCapture={stopPointerEvent}
							style={
								{
									fontSize: '0.8em',
									tableLayout: 'auto',
									backgroundColor: 'transparent !important',
									'--bs-table-bg': 'transparent',
									userSelect: 'text',
									WebkitUserSelect: 'text',
									MozUserSelect: 'text',
									msUserSelect: 'text',
								} as React.CSSProperties
							}
						>
							<thead>
								<tr>
									{displayScore && <th style={{ width: 'auto' }}>Score</th>}
									<th style={{ width: 'auto' }}>Synonymtreff</th>
									<th style={{ width: 'auto' }}>Synonym av</th>
									<th style={{ width: 'auto' }}>ID</th>
									<th style={{ width: 'auto' }}>System</th>
								</tr>
							</thead>
							<tbody>
								{synonymHits.map((hit, i) => {
									return (
										(i < config.maximumNestedFieldValues || showAllHits) && (
											<tr key={`${hit}-${i}`}>
												{displayScore && (
													<td style={{ whiteSpace: 'nowrap' }}>{hit.score}</td>
												)}
												<td style={{ whiteSpace: 'nowrap' }}>
													{parse(hit.term)}
												</td>
												<td>{parse(hit.synonym_of_term)}</td>
												<td>{parse(hit.source_id)}</td>
												<td>{parse(hit.source_system)}</td>
											</tr>
										)
									)
								})}

								<tr>
									<td
										colSpan={displayScore ? 5 : 4}
										className='px-3 text-start'
										style={{
											color: '#6c757d',
											fontSize: '0.85em',
										}}
									>
										<span>
											Viser {totalHits} av {totalHits} synonymtreff
										</span>
									</td>
								</tr>
							</tbody>
						</Table>
					)}
				</Row>
			)}
		</>
	)
}

export default SearchResultRow
